import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Car, Gauge, Timer, Zap, Octagon, Coffee, Wine, ArrowRight, Sparkles } from 'lucide-react';
import { FadeUp, Stagger, StaggerItem } from './MotionReveals';

const PREVIEW = [
  { slug: 'mariotte', title: 'Tache aveugle', sub: 'La croix qui disparaît quand on ferme un œil', icon: Eye, accent: 'from-red-600 to-red-800' },
  { slug: 'vehicle-blindspot', title: 'Angles morts', sub: 'Drag le cycliste autour de la voiture (vue du dessus)', icon: Car, accent: 'from-orange-600 to-red-700' },
  { slug: 'reaction-time', title: 'Temps de réaction', sub: 'Clique au vert · combien de mètres à 90 km/h ?', icon: Timer, accent: 'from-emerald-600 to-emerald-800' },
  { slug: 'alcohol-vision', title: 'Vision sous alcool', sub: 'Slider 0→1,2 g/L · la scène se floute et dédouble', icon: Wine, accent: 'from-purple-600 to-purple-900' },
  { slug: 'visual-tunnel', title: 'Effet tunnel', sub: 'À 130 km/h, ton champ visuel se réduit à 30°', icon: Gauge, accent: 'from-blue-600 to-blue-900' },
  { slug: 'signs-discrimination', title: 'Panneaux flash', sub: 'Identifie un panneau flashé 600 ms · 5 manches', icon: Octagon, accent: 'from-yellow-600 to-orange-800' },
  { slug: 'fatigue', title: 'Test fatigue (PVT)', sub: '30 s · méthode NASA · détecte ta vigilance', icon: Coffee, accent: 'from-amber-700 to-amber-900' },
  { slug: 'kinetic', title: 'Énergie cinétique', sub: 'Double la vitesse → quadruple l\'énergie', icon: Zap, accent: 'from-rose-600 to-rose-900' },
];

export default function FeaturedDemosSection({ variant = 'home' }) {
  // variant: 'home' (full intro) | 'dashboard' (compact, for connected users)
  const isCompact = variant === 'dashboard';
  return (
    <section
      className={`max-w-7xl mx-auto px-4 lg:px-6 ${isCompact ? 'mt-8 sm:mt-10' : 'mt-14 sm:mt-24'}`}
      data-testid="featured-demos-section"
    >
      <FadeUp>
        <div className="relative rounded-2xl sm:rounded-3xl border border-red-700/40 bg-gradient-to-br from-red-950/40 via-[#150a0a] to-[#0d0d0e] p-5 sm:p-7 lg:p-10 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-red-600/15 blur-3xl pointer-events-none" />
          <motion.div
            className="absolute -bottom-24 left-1/3 w-72 h-72 rounded-full bg-red-500/10 blur-3xl pointer-events-none"
            animate={{ opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="relative">
            <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-300 font-bold">8 démos pédagogiques · uniques en France</span>
            </div>

            <h2 className={`font-serif-editorial text-white tracking-tight leading-[1.02] ${
              isCompact
                ? 'text-[22px] sm:text-[30px] lg:text-[38px]'
                : 'text-[26px] sm:text-[40px] lg:text-[56px]'
            }`}>
              {isCompact
                ? <>Teste tes <span className="text-red-500">réflexes</span>, ta <span className="text-red-500">vision</span>, ta <span className="text-red-500">vigilance</span>.</>
                : <>Comprends en 30 secondes<br /><span className="text-red-500">ce qu'un cours met 30 minutes.</span></>
              }
            </h2>

            <p className="mt-3 sm:mt-4 text-white/70 text-[13.5px] sm:text-[15px] max-w-3xl leading-relaxed">
              {isCompact
                ? 'Découvre ta tache aveugle, les vrais angles morts d\'une voiture, ton temps de réaction, et l\'impact réel d\'1 g/L d\'alcool sur ta vision. Interactives · à projeter en salle · gratuites.'
                : '8 démonstrations interactives calibrées par des moniteurs ECSR. Tache aveugle de Mariotte, angles morts (vue du dessus), effet tunnel selon la vitesse, test PVT NASA, vision sous alcool… Tout est interactif, gratuit, sans inscription.'
              }
            </p>

            {/* Demo grid preview */}
            <Stagger
              className={`grid gap-2.5 sm:gap-3 mt-5 sm:mt-7 ${
                isCompact ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
              }`}
              staggerChildren={0.05}
            >
              {PREVIEW.slice(0, isCompact ? 4 : 8).map((d) => {
                const Icon = d.icon;
                return (
                  <StaggerItem key={d.slug}>
                    <Link
                      to={`/demos-interactives#${d.slug}`}
                      data-testid={`featured-demo-${d.slug}`}
                      className={`group block relative rounded-xl overflow-hidden border border-white/10 bg-gradient-to-br ${d.accent} p-3 sm:p-4 h-full hover:scale-[1.03] active:scale-95 transition-transform shadow-lg shadow-black/30`}
                    >
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center text-white mb-2">
                        <Icon size={18} />
                      </div>
                      <div className="font-serif-editorial text-white text-[14px] sm:text-[17px] leading-tight">{d.title}</div>
                      <div className="text-white/80 text-[10.5px] sm:text-[11.5px] mt-1 line-clamp-2 leading-tight">{d.sub}</div>
                    </Link>
                  </StaggerItem>
                );
              })}
            </Stagger>

            <div className="mt-5 sm:mt-7 flex flex-wrap items-center gap-3 sm:gap-4">
              <Link
                to="/demos-interactives"
                data-testid="featured-demos-cta"
                className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold shadow-xl shadow-red-900/40 hover:scale-[1.03] active:scale-95 transition"
              >
                Découvrir les 8 démos <ArrowRight size={15} />
              </Link>
              <div className="text-white/55 text-[11.5px] sm:text-[12.5px] font-mono-spec">
                Gratuit · sans inscription · projetable en salle
              </div>
            </div>
          </div>
        </div>
      </FadeUp>
    </section>
  );
}
